import { Component, ErrorInfo, ReactNode } from 'react';
import { captureError, addBreadcrumb } from '../lib/sentry';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error info
    this.setState({
      errorInfo,
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Report to Sentry
    addBreadcrumb({
      category: 'error-boundary',
      message: 'Error caught by ErrorBoundary',
      level: 'error',
      data: {
        componentStack: errorInfo.componentStack,
      },
    });

    const eventId = captureError(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    });

    if (eventId) {
      this.setState({ eventId });
    }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-coral-50 to-teal-50 p-4">
          <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8">
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="bg-gradient-to-br from-red-100 to-orange-100 rounded-full p-4">
                <svg
                  className="w-16 h-16 text-red-600"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>
            </div>

            {/* Error Message */}
            <h1 className="text-3xl font-bold text-center text-gray-800 mb-4">
              Oops! Something went wrong
            </h1>
            <p className="text-center text-gray-600 mb-8 text-lg">
              We're sorry for the inconvenience. Our team has been notified and we're working on fixing this issue.
            </p>

            {/* Error Details (Development only) */}
            {this.props.showDetails && import.meta.env.DEV && this.state.error && (
              <div className="mb-8 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <details>
                  <summary className="cursor-pointer font-semibold text-gray-700 mb-2">
                    Error Details (Development Mode)
                  </summary>
                  <div className="space-y-2 mt-4">
                    <div>
                      <p className="font-semibold text-sm text-gray-700 mb-1">Error Message:</p>
                      <pre className="text-xs bg-red-50 p-3 rounded border border-red-200 overflow-x-auto text-red-800">
                        {this.state.error.toString()}
                      </pre>
                    </div>
                    {this.state.errorInfo && (
                      <div>
                        <p className="font-semibold text-sm text-gray-700 mb-1">Component Stack:</p>
                        <pre className="text-xs bg-gray-100 p-3 rounded border border-gray-300 overflow-x-auto text-gray-700">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={this.handleReset}
                className="btn-primary text-white font-semibold py-3 px-6 rounded-lg hover:opacity-90 transition-all transform hover:scale-105"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="bg-gray-100 text-gray-700 font-semibold py-3 px-6 rounded-lg hover:bg-gray-200 transition-all"
              >
                Go to Home
              </button>
            </div>

            {/* Support Information */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-center text-sm text-gray-600">
                If this problem persists, please{' '}
                <a
                  href="/contact"
                  className="text-coral-700 hover:text-coral-800 font-semibold underline"
                >
                  contact our support team
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Functional wrapper for easier usage with hooks
interface ErrorBoundaryWrapperProps extends Omit<ErrorBoundaryProps, 'onError'> {
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  fallbackComponent?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

export const ErrorBoundaryWrapper = ({
  children,
  fallback,
  fallbackComponent: FallbackComponent,
  onError,
  showDetails = true,
}: ErrorBoundaryWrapperProps) => {
  const handleError = (error: Error, errorInfo: ErrorInfo) => {
    onError?.(error, errorInfo);
  };

  return (
    <ErrorBoundary
      fallback={fallback}
      onError={handleError}
      showDetails={showDetails}
    >
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
